import React, { useState } from "react";
import { motion } from "framer-motion";
import { ConsentCheckboxBox } from "./Account/ConsentCheckbox";
import type { CookieConsentPrefs } from "../utils/analyticsConsent";

type CookieConsentModalProps = {
  onDecision: (prefs: CookieConsentPrefs) => void;
};

export function CookieConsentModal({ onDecision }: CookieConsentModalProps) {
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);

  const acceptAll = () => onDecision({ analytics: true, marketing: true });
  const rejectAll = () => onDecision({ analytics: false, marketing: false });
  const saveChoices = () => onDecision({ analytics, marketing });

  // Mixed selection → Accept selected; both on or both off → Accept All.
  const hasMixedSelection = analytics !== marketing;
  const primaryLabel = hasMixedSelection ? "Accept selected" : "Accept All";
  const primaryAction = hasMixedSelection ? saveChoices : acceptAll;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-consent-title"
    >
      <motion.div
        className="relative border-2 border-[#D2FF00] bg-[#2a1a3d] shadow-xl p-6 md:p-8 max-w-md w-full"
        initial={{ opacity: 0, scale: 0.85, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 280, damping: 22 }}
      >
        <h2
          id="cookie-consent-title"
          className="text-[#D2FF00] font-anton text-[18px] md:text-[22px] uppercase mb-2"
        >
          Your privacy matters
        </h2>
        <p className="text-white font-chakra text-[14px] md:text-[16px] normal-case mb-6">
          We use essential cookies to keep Cricket8 secure and your games running smoothly. With your
          permission, we&apos;d also like to use optional cookies to understand how players use Cricket8 and
          improve their experience.
        </p>

        <ul className="flex flex-col border border-white/15 divide-y divide-white/15">
          <li className="flex items-center justify-between gap-3 px-3 py-3 sm:px-4">
            <div className="min-w-0">
              <p className="font-chakra text-[14px] font-semibold normal-case text-white">Essential Cookies</p>
              <p className="font-chakra text-[12px] font-normal normal-case text-white/65">
                Required for security, login and gameplay.
              </p>
            </div>
            <span className="shrink-0 bg-[#32F78D]/15 px-2 py-1 font-chakra text-[11px] font-semibold normal-case text-[#32F78D]">
              Always active
            </span>
          </li>

          <li>
            <button
              type="button"
              className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left sm:px-4 hover:bg-white/5"
              aria-pressed={analytics}
              onClick={() => setAnalytics((v) => !v)}
            >
              <div className="min-w-0">
                <p className="font-chakra text-[14px] font-semibold normal-case text-white">
                  Analytics Cookies
                </p>
                <p className="font-chakra text-[12px] font-normal normal-case text-white/65">
                  Help us understand and improve Cricket8.
                </p>
              </div>
              <ConsentCheckboxBox checked={analytics} />
            </button>
          </li>

          <li>
            <button
              type="button"
              className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left sm:px-4 hover:bg-white/5"
              aria-pressed={marketing}
              onClick={() => setMarketing((v) => !v)}
            >
              <div className="min-w-0">
                <p className="font-chakra text-[14px] font-semibold normal-case text-white">
                  Marketing Cookies
                </p>
                <p className="font-chakra text-[12px] font-normal normal-case text-white/65">
                  Help us understand which campaigns work.
                </p>
              </div>
              <ConsentCheckboxBox checked={marketing} />
            </button>
          </li>
        </ul>

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={rejectAll}
            className="min-h-11 px-2 font-chakra text-[14px] font-semibold normal-case text-white/80 hover:text-white transition-colors"
          >
            Reject All
          </button>
          <button
            type="button"
            onClick={primaryAction}
            className="w-full sm:min-w-[200px] sm:w-auto py-3 px-8 bg-[#D2FF00] text-[#1B062F] font-anton text-[16px] uppercase hover:bg-[#B8E600] transition-colors"
          >
            {primaryLabel}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
